import { View, Text, Pressable, ActivityIndicator } from 'react-native';
import { footballerQuizViewStyles as styles } from './FootballerQuizView.styles';
import { FootballerQuizBoard } from './FootballerQuizBoard';
import { FootballerQuizResultOverlay } from '../FootballerQuizResultOverlay/FootballerQuizResultOverlay';
import { WarmupToast } from '../WarmupToast/WarmupToast';
import { useFootballerQuizGame } from '../../../src/quiz/hooks/useFootballerQuizGame';
import { shareResult } from '../../../src/quiz/helpers/shareResult.helper';
import { useAnalytics } from '../../../src/analytics/useAnalytics';
import { ANALYTICS_EVENTS } from '../../../src/analytics/events';
import { WARMUP_COLORS } from '../../../src/quiz/constants/warmup.constants';
import { FOOTBALLER_GAME_KEY, FOOTBALLER_TEST_IDS as IDS } from '../../../src/quiz/constants/footballerQuiz.constants';

interface FootballerQuizViewProps {
  onMoreGames: () => void;
  onLeaderboard: () => void;
}

export function FootballerQuizView({ onMoreGames, onLeaderboard }: FootballerQuizViewProps) {
  const game = useFootballerQuizGame();
  const { track } = useAnalytics();

  const onShare = async () => {
    if (!game.result) return;
    const res = await shareResult(game.result.share);
    if (res === 'copied') game.flashToast('Copied to clipboard 🙌');
    else if (res === 'failed') game.flashToast('Could not share');
    if (res !== 'failed') track(ANALYTICS_EVENTS.GAME_SHARED, { game_key: FOOTBALLER_GAME_KEY });
  };

  if (game.isLoading) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator color={WARMUP_COLORS.purple} />
      </View>
    );
  }

  if (game.isError) {
    return (
      <View style={styles.centered}>
        <Text style={styles.message}>{game.errorMessage ?? "Couldn't load today's game."}</Text>
        <Pressable onPress={() => game.retry()} style={({ pressed }) => [styles.retryButton, pressed && styles.pressed]}>
          <Text style={styles.retryText}>Retry</Text>
        </Pressable>
      </View>
    );
  }

  return (
    <View style={styles.content} testID={IDS.view}>
      <FootballerQuizBoard
        streak={game.streak}
        maxGuesses={game.maxGuesses}
        guessesUsed={game.guessesUsed}
        guessesLeft={game.guessesLeft}
        guesses={game.guesses}
        input={game.input}
        suggestions={game.suggestions}
        showDropdown={game.showDropdown}
        busy={game.busy}
        onInputChange={game.onInputChange}
        onSelectSuggestion={game.onSelectSuggestion}
        onSubmit={game.onSubmit}
        onFocus={game.onFocus}
        onBlur={game.onBlur}
      />

      <FootballerQuizResultOverlay
        visible={game.overlayOpen}
        result={game.result}
        guesses={game.guesses}
        maxGuesses={game.maxGuesses}
        onShare={onShare}
        onLeaderboard={onLeaderboard}
        onNext={onMoreGames}
      />

      <WarmupToast message={game.toast} />
    </View>
  );
}
